import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import * as lessonApi from '@/api/lesson'
import type { Lesson } from '@/types/lesson'
import type { MicrocourseProject, MicroSlide } from '@ai-tutor/shared'
import { normalizeDeck } from '@ai-tutor/shared'
import { SlideCanvas } from '@/components/microcourse'

/**
 * Printable lesson view — one slide per page.
 * Uses unified MicrocourseProject + SlideCanvas rendering.
 * User can Ctrl+P to save as PDF.
 */
export default function LessonPrintPage() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!lessonId) return
    lessonApi
      .getLesson(lessonId)
      .then((raw: any) => {
        const normalized = { ...raw, deck: raw.deck ? normalizeDeck(raw.deck, raw.id) : null }
        setLesson(normalized)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [lessonId])

  useEffect(() => {
    if (lesson && !loading) {
      // Auto-trigger print dialog after a short delay
      const timer = setTimeout(() => window.print(), 500)
      return () => clearTimeout(timer)
    }
  }, [lesson, loading])

  if (loading) return <div className="p-8 text-center">加载中...</div>
  if (!lesson?.deck) return <div className="p-8 text-center">课件不存在</div>

  const project = lesson.deck as MicrocourseProject
  const slides = [...project.slides].sort((a, b) => a.order - b.order)

  return (
    <div className="print:p-0">
      <style>{`
        @media print {
          body { margin: 0; }
          .slide-page {
            page-break-after: always;
            page-break-inside: avoid;
            min-height: 100vh;
            padding: 2cm;
            box-sizing: border-box;
          }
          .slide-page:last-child { page-break-after: auto; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Print button (hidden when printing) */}
      <div className="no-print fixed top-4 right-4 z-50">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          🖨️ 打印 / 保存 PDF
        </button>
      </div>

      {/* Title page */}
      <div className="slide-page">
        <div className="max-w-[800px] mx-auto flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-3xl font-bold mb-4">{project.title}</h1>
          <p className="text-gray-400 text-sm">共 {slides.length} 页</p>
        </div>
      </div>

      {slides.map((slide, i) => (
        <div key={slide.id} className="slide-page">
          <SlidePrintView slide={slide} index={i} total={slides.length} />
        </div>
      ))}
    </div>
  )
}

function SlidePrintView({
  slide,
  index,
  total,
}: {
  slide: MicroSlide
  index: number
  total: number
}) {
  return (
    <div className="max-w-[800px] mx-auto">
      {/* Slide number */}
      <div className="text-sm text-gray-400 mb-4">
        {index + 1} / {total}
      </div>

      {/* Render all elements via SlideCanvas (fragmentIndex=-1 shows all) */}
      <div className="min-h-[200px]">
        <SlideCanvas slide={slide} fragmentIndex={-1} />
      </div>

      {/* Speaker notes / narration text */}
      {slide.notes && (
        <div className="mt-8 pt-4 border-t text-sm text-gray-500">
          <div className="font-medium mb-1">教师讲解参考：</div>
          <div className="whitespace-pre-wrap">{slide.notes}</div>
        </div>
      )}

      {/* Segments narration text (if no notes but has segments) */}
      {!slide.notes && slide.segments.length > 0 && (
        <div className="mt-8 pt-4 border-t text-sm text-gray-500">
          <div className="font-medium mb-1">讲解内容：</div>
          {slide.segments.map((seg, i) => (
            <p key={i} className="mb-1">{seg.ttsText}</p>
          ))}
        </div>
      )}
    </div>
  )
}
